<template>
  <div class="pub">
    <div class="container">
      <div v-if="preview" class="previewBanner">
        <span class="previewBadge">{{
          $t("managedInstructionAssignment.instructionPublic.preview.badge")
        }}</span>
        <span class="previewNote">{{
          $t("managedInstructionAssignment.instructionPublic.preview.note")
        }}</span>
        <select
          v-if="availablePreviewLangs.length > 1"
          v-model="employeeLanguage"
          class="previewLang"
          @change="onPreviewLangChange"
        >
          <option v-for="l in availablePreviewLangs" :key="l" :value="l">
            {{ l }}
          </option>
        </select>
        <button class="btn ghost sm previewRestart" @click="restartPreview">
          {{
            $t("managedInstructionAssignment.instructionPublic.preview.restart")
          }}
        </button>
      </div>

      <div v-if="loading" class="card">
        {{ $t("managedInstructionAssignment.instructionPublic.loading") }}
      </div>

      <div v-else-if="error" class="card error">
        <h2>
          {{
            $t(
              "managedInstructionAssignment.instructionPublic.invalidLink.title",
            )
          }}
        </h2>
        <p>{{ error }}</p>
      </div>

      <div v-else-if="completed" class="card">
        <h2 style="margin: 0 0 8px 0">
          {{
            $t("managedInstructionAssignment.instructionPublic.completed.title")
          }}
        </h2>
        <p class="muted" style="margin: 0">
          {{
            $t(
              "managedInstructionAssignment.instructionPublic.completed.subtitle",
            )
          }}
        </p>
      </div>

      <div v-else class="layout">
        <aside class="sidebar">
          <div class="card headerCard">
            <img
              v-if="previewImageSrc"
              class="preview"
              :src="previewImageSrc"
              alt=""
            />
            <h1 class="title">{{ localizedName }}</h1>
            <p class="desc" v-if="localizedDescription">
              {{ localizedDescription }}
            </p>
            <div class="meta">
              <span class="badge" :class="'st-' + assignment.status">
                {{ assignment.status }}
              </span>
            </div>

            <div class="progressWrap">
              <div class="progressText">
                {{
                  $t(
                    "managedInstructionAssignment.instructionPublic.progress.step",
                    {
                      current: stepIndex + 1,
                      total: steps.length,
                    },
                  )
                }}
              </div>
              <div class="progressBar">
                <div
                  class="progressFill"
                  :style="{ width: progressPercent + '%' }"
                ></div>
              </div>
            </div>

            <div class="unlockHint" v-if="steps.length">
              {{
                $t(
                  "managedInstructionAssignment.instructionPublic.progress.unlocked",
                  {
                    unlocked: unlockedMaxIndex + 1,
                    total: steps.length,
                  },
                )
              }}
            </div>
          </div>

          <div class="card stepsCard" ref="stepsCard">
            <div
              v-for="(s, idx) in steps"
              :key="idx"
              ref="stepRows"
              class="stepRow"
              :class="{
                active: idx === stepIndex,
                done: idx < stepIndex,
                locked: idx > unlockedMaxIndex,
              }"
              @click="onStepClick(idx)"
              :title="
                idx > unlockedMaxIndex
                  ? $t(
                      'managedInstructionAssignment.instructionPublic.steps.lockedTooltip',
                    )
                  : ''
              "
            >
              <span class="icon">
                {{ idx > unlockedMaxIndex ? "🔒" : iconFor(s.type) }}
              </span>
              <span class="label"
                >{{
                  $t("managedInstructionAssignment.instructionPublic.step")
                }}
                {{ idx + 1 }} · {{ labelFor(s.type) }}</span
              >
            </div>
          </div>
        </aside>

        <main class="main">
          <div class="card contentCard">
            <div class="stepTop">
              <div class="stepTitle">{{ labelFor(currentStep.type) }}</div>
              <div class="stepHint" v-if="hintFor(currentStep.type)">
                {{ hintFor(currentStep.type) }}
              </div>

              <p v-if="stepUrlError" class="errorText" style="margin-top: 8px">
                {{ stepUrlError }}
              </p>
            </div>

            <div v-if="currentStep.type === 'pdf'" class="pdfContainer">
              <div class="fileRow">
                <button
                  class="btn ghost sm"
                  :disabled="stepUrlLoading || !resolvedDownloadUrl"
                  @click="downloadResolved()"
                >
                  {{
                    $t(
                      "managedInstructionAssignment.instructionPublic.buttons.download",
                    )
                  }}
                </button>
                <button
                  class="btn ghost sm"
                  :disabled="stepUrlLoading || !resolvedStepUrl"
                  @click="openFullscreen()"
                >
                  {{
                    $t(
                      "managedInstructionAssignment.instructionPublic.buttons.fullscreen",
                    )
                  }}
                </button>
              </div>

              <div class="pdfWrapper" ref="viewerWrapper">
                <pdfMobileViewer
                  v-if="resolvedStepUrl && useMobilePdf && !pdfViewerFailed"
                  :src="resolvedStepUrl"
                  @error="pdfViewerFailed = true"
                />
                <iframe
                  v-else-if="resolvedStepUrl"
                  :src="resolvedStepUrl"
                  class="pdfFrame"
                  type="application/pdf"
                  frameborder="0"
                >
                  <p>
                    {{
                      $t(
                        "managedInstructionAssignment.instructionPublic.pdf.noEmbed",
                      )
                    }}
                    <a :href="resolvedDownloadUrl" target="_blank">
                      {{
                        $t(
                          "managedInstructionAssignment.instructionPublic.pdf.downloadHere",
                        )
                      }} </a
                    >.
                  </p>
                </iframe>

                <div v-else-if="stepUrlLoading" class="loadingPlaceholder">
                  {{
                    $t(
                      "managedInstructionAssignment.instructionPublic.placeholders.documentLoading",
                    )
                  }}
                </div>
              </div>
            </div>

            <div
              v-else-if="
                currentStep.type === 'mov' ||
                currentStep.type === 'mp4' ||
                currentStep.type === 'mkv'
              "
            >
              <video
                v-if="resolvedStepUrl"
                class="video"
                controls
                playsinline
                :src="resolvedStepUrl"
              />
              <div v-else-if="stepUrlLoading" class="loadingPlaceholder">
                {{
                  $t(
                    "managedInstructionAssignment.instructionPublic.placeholders.videoLoading",
                  )
                }}
              </div>
            </div>

            <div v-else-if="currentStep.type === 'singleChoiceQuestion'">
              <div class="question">
                <h2 class="q">{{ questionText }}</h2>

                <div class="options">
                  <label
                    v-for="opt in questionOptions"
                    :key="opt.index"
                    class="option"
                    :class="{ selected: selectedAnswer === opt.index }"
                  >
                    <input
                      type="radio"
                      name="answer"
                      :value="opt.index"
                      v-model.number="selectedAnswer"
                    />
                    <span>{{ opt.text }}</span>
                  </label>
                </div>

                <p v-if="qError" class="errorText">{{ qError }}</p>

                <button
                  v-if="qError && stepIndex > 0"
                  class="btn ghost sm"
                  style="margin-top: 8px"
                  @click="prevStep()"
                >
                  {{
                    $t(
                      "managedInstructionAssignment.instructionPublic.buttons.goPrevStep",
                    )
                  }}
                </button>
              </div>
            </div>

            <div
              v-else-if="currentStep.type === 'link'"
              class="linkStep"
            >
              <h2 class="linkTitle">{{ linkTitle }}</h2>
              <p class="linkText" v-if="linkText">{{ linkText }}</p>

              <a
                v-if="linkUrl"
                class="btn primary linkBtn"
                :href="linkUrl"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{
                  $t(
                    "managedInstructionAssignment.instructionPublic.buttons.openResource",
                  )
                }}
              </a>
            </div>

            <div
              v-else-if="
                currentStep.type === 'doc' ||
                currentStep.type === 'docx' ||
                currentStep.type === 'ppt' ||
                currentStep.type === 'pptx'
              "
            >
              <div class="fileRow">
                <button
                  class="btn ghost sm"
                  :disabled="stepUrlLoading || !resolvedDownloadUrl"
                  @click="downloadResolved()"
                >
                  {{
                    $t(
                      "managedInstructionAssignment.instructionPublic.buttons.download",
                    )
                  }}
                </button>
                <button
                  class="btn ghost sm"
                  :disabled="stepUrlLoading || !resolvedStepUrl"
                  @click="openFullscreen()"
                >
                  {{
                    $t(
                      "managedInstructionAssignment.instructionPublic.buttons.fullscreen",
                    )
                  }}
                </button>
              </div>

              <div class="pdfWrapper" ref="viewerWrapper" v-if="resolvedStepUrl">
                <iframe
                  class="pdfFrame"
                  :src="officeViewerUrl(resolvedStepUrl)"
                  frameborder="0"
                />
              </div>

              <div v-else-if="stepUrlLoading" class="loadingPlaceholder">
                {{
                  $t(
                    "managedInstructionAssignment.instructionPublic.placeholders.documentLoading",
                  )
                }}
              </div>
            </div>

            <div v-else>
              <p>
                {{
                  $t(
                    "managedInstructionAssignment.instructionPublic.unsupported.stepType",
                    {
                      type: currentStep.type,
                    },
                  )
                }}
              </p>
            </div>
          </div>

          <div class="navBar">
            <button
              class="btn ghost"
              :disabled="stepIndex === 0"
              @click="prevStep"
            >
              {{
                $t(
                  "managedInstructionAssignment.instructionPublic.buttons.back",
                )
              }}
            </button>

            <div class="navRight">
              <button
                v-if="!isLastStep"
                class="btn"
                :disabled="nextDisabled"
                @click="nextStep"
              >
                {{
                  $t(
                    "managedInstructionAssignment.instructionPublic.buttons.next",
                  )
                }}
              </button>

              <button
                v-else
                class="btn primary"
                :disabled="completeDisabled"
                @click="complete"
              >
                {{
                  $t(
                    "managedInstructionAssignment.instructionPublic.buttons.complete",
                  )
                }}
              </button>
            </div>
          </div>
        </main>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import pdfMobileViewer from "./pdfMobileViewer.vue";

const PUBLIC_PREFIX = "https://s3.cloud2.c-dev.io/sp-templating";

export default {
  name: "InstructionPublic",
  components: { pdfMobileViewer },
  props: {
    token: { type: String, default: "" },
    previewInstructionId: { type: String, default: "" },
    previewType: { type: String, default: "" },
  },

  data() {
    return {
      preview: false,
      rawInstruction: null,
      completed: false,
      loading: true,
      error: "",
      assignment: null,
      instruction: null,
      steps: [],
      stepIndex: 0,
      answers: {},
      selectedAnswer: null,
      qError: "",
      submitting: false,
      previewImageSrc: "",
      previewImageLoading: false,
      resolvedStepUrl: "",
      resolvedDownloadUrl: "",
      stepUrlLoading: false,
      stepUrlError: "",
      unlockedMaxIndex: 0,
      employeeLanguage: "GER",
      useMobilePdf: false,
      pdfViewerFailed: false,
    };
  },

  computed: {
    localizedName() {
      return this.pickLocalized(
        this.instruction?.name,
        this.employeeLanguage,
        "name",
      );
    },
    localizedDescription() {
      return this.pickLocalized(
        this.instruction?.description,
        this.employeeLanguage,
        "description",
      );
    },

    currentStep() {
      return this.steps[this.stepIndex] || { type: "unknown", data: [] };
    },
    isLastStep() {
      return this.stepIndex >= this.steps.length - 1;
    },
    progressPercent() {
      if (!this.steps.length) return 0;
      return Math.round(((this.stepIndex + 1) / this.steps.length) * 100);
    },

    questionEntry() {
      return (this.currentStep.data && this.currentStep.data[0]) || {};
    },
    questionId() {
      return this.questionEntry?._id?.$oid || this.questionEntry?._id || "";
    },
    questionText() {
      return this.questionEntry?.question || "";
    },

    questionOptions() {
      const answers = Array.isArray(this.questionEntry?.answers)
        ? this.questionEntry.answers
        : [];
      return answers
        .map((text, index) => ({ index, text: (text ?? "").toString() }))
        .filter((o) => o.text.trim().length > 0);
    },

    linkEntry() {
      return (this.currentStep.data && this.currentStep.data[0]) || {};
    },
    linkTitle() {
      return this.linkEntry?.title || "";
    },
    linkText() {
      return this.linkEntry?.text || "";
    },
    linkUrl() {
      return this.linkEntry?.url || "";
    },

    availablePreviewLangs() {
      const blocks = Array.isArray(this.rawInstruction?.steps)
        ? this.rawInstruction.steps
        : [];
      const langs = blocks
        .map((b) => b?.language)
        .filter((l) => l === "GER" || l === "ENG");
      return langs.length ? Array.from(new Set(langs)) : ["GER"];
    },

    nextDisabled() {
      if (this.isLastStep) return true;
      if (
        this.requiresAnswer() &&
        (this.selectedAnswer === null || this.selectedAnswer === undefined)
      )
        return true;
      return false;
    },

    completeDisabled() {
      return (
        this.submitting ||
        (this.requiresAnswer() &&
          (this.selectedAnswer === null || this.selectedAnswer === undefined))
      );
    },
  },

  watch: {
    "instruction.previewImage": {
      immediate: true,
      handler() {
        this.resolvePreviewImage();
      },
    },
    stepIndex: {
      immediate: true,
      handler() {
        this.resolveCurrentStepUrl();
        this.scrollActiveStepIntoView();
      },
    },
    steps: {
      deep: true,
      handler() {
        this.resolveCurrentStepUrl();
        this.scrollActiveStepIntoView();
      },
    },
  },

  mounted() {
    this.mobileQuery = window.matchMedia("(max-width: 900px), (pointer: coarse)");
    this.useMobilePdf = this.mobileQuery.matches;
    this.onMobileQueryChange = (e) => {
      this.useMobilePdf = e.matches;
    };
    if (this.mobileQuery.addEventListener) {
      this.mobileQuery.addEventListener("change", this.onMobileQueryChange);
    } else {
      this.mobileQuery.addListener(this.onMobileQueryChange);
    }
  },

  beforeDestroy() {
    if (!this.mobileQuery) return;
    if (this.mobileQuery.removeEventListener) {
      this.mobileQuery.removeEventListener("change", this.onMobileQueryChange);
    } else {
      this.mobileQuery.removeListener(this.onMobileQueryChange);
    }
  },

  async created() {
    this.preview = !!this.previewInstructionId;

    if (this.preview) {
      try {
        await this.loadPreview();
      } catch (e) {
        this.error =
          e?.response?.data?.message ||
          this.$t(
            "managedInstructionAssignment.instructionPublic.errors.cannotOpenLink",
          );
      } finally {
        this.loading = false;
      }
      return;
    }

    try {
      if (!this.token || this.token.length < 32) {
        throw new Error(
          this.$t(
            "managedInstructionAssignment.instructionPublic.errors.missingToken",
          ),
        );
      }

      const res = await axios.get("/managedInstruction/public", {
        params: { token: this.token },
      });

      if (res.data.assignment.status === "done") {
        this.completed = true;
      } else if (res.data.assignment.status === "assigned") {
        const status = await axios.get("/managedInstruction/publicOpened", {
          params: { token: this.token },
        });
        if (status.data.ok === true) {
          res.data.assignment.status = "open";
        }
      }

      this.assignment = res.data.assignment;
      this.instruction = res.data.instruction;

      this.employeeLanguage = this.normalizeEmployeeLanguage(
        res.data.assignment?.employee?.employeeLanguage ||
          res.data.instruction?.stepsLanguage ||
          this.guessEmployeeLanguageFromBrowser(),
      );

      this.applyI18nLocale();

      this.steps = Array.isArray(this.instruction?.steps)
        ? this.instruction.steps
        : [];

      const progress = res.data.progress || {};
      this.answers = progress.answers || {};
      this.unlockedMaxIndex = progress.unlockedMaxIndex ?? 0;

      const incomingIndex = progress.currentStepIndex || 0;
      const maxIndex = Math.max(0, this.steps.length - 1);
      this.stepIndex = Math.min(incomingIndex, this.unlockedMaxIndex, maxIndex);

      this.syncSelectedFromAnswers();
      this.scrollActiveStepIntoView();
    } catch (e) {
      this.error =
        e?.response?.data?.message ||
        this.$t(
          "managedInstructionAssignment.instructionPublic.errors.cannotOpenLink",
        );
    } finally {
      this.loading = false;
    }
  },

  methods: {
    i18nLocaleFromEmployeeLanguage(lang) {
      return lang === "ENG" ? "en" : "de";
    },

    applyI18nLocale() {
      const locale = this.i18nLocaleFromEmployeeLanguage(this.employeeLanguage);

      if (this.$i18n && this.$i18n.locale !== locale) {
        this.$i18n.locale = locale;
      }
    },

    async loadPreview() {
      if (this.previewType === "global") {
        // Global instruction: employees see the client's local (merged) steps —
        // inherited "general" steps plus the client's own additions.
        const data = await this.$store.dispatch("getManagedInstructionLocal", {
          _id: this.previewInstructionId,
        });

        const instruction = data?.instruction || {};
        this.rawInstruction = { steps: data?.local?.steps || [] };
        this.instruction = {
          _id: instruction._id,
          type: instruction.type,
          name: instruction.name,
          description: instruction.description,
          previewImage: instruction.previewImage,
          previewImageId: instruction.previewImageId,
        };
      } else {
        const { data } = await this.$store.dispatch(
          "getManagedInstructionById",
          this.previewInstructionId,
        );

        this.rawInstruction = data;
        this.instruction = {
          _id: data?._id,
          type: data?.type,
          name: data?.name,
          description: data?.description,
          previewImage: data?.previewImage,
          previewImageId: data?.previewImageId,
        };
      }

      this.assignment = { status: "preview" };

      this.employeeLanguage = this.availablePreviewLangs.includes("GER")
        ? "GER"
        : this.availablePreviewLangs[0];

      this.applyI18nLocale();
      this.applyPreviewLang();
    },

    applyPreviewLang() {
      const blocks = Array.isArray(this.rawInstruction?.steps)
        ? this.rawInstruction.steps
        : [];
      const bucket =
        blocks.find((b) => b?.language === this.employeeLanguage) ||
        blocks.find((b) => b?.language === "GER") ||
        blocks[0];

      this.completed = false;
      this.answers = {};
      this.selectedAnswer = null;
      this.qError = "";
      this.unlockedMaxIndex = 0;
      this.stepIndex = 0;
      this.steps = Array.isArray(bucket?.steps) ? bucket.steps : [];
    },

    onPreviewLangChange() {
      this.applyI18nLocale();
      this.applyPreviewLang();
    },

    restartPreview() {
      this.applyPreviewLang();
    },
    normalizeEmployeeLanguage(lang) {
      if (lang === "GER" || lang === "ENG") return lang;
      const l = (lang || "").toString().toLowerCase();
      if (l.startsWith("de")) return "GER";
      if (l.startsWith("en")) return "ENG";
      return "GER";
    },

    guessEmployeeLanguageFromBrowser() {
      const nav = (navigator.language || "").toLowerCase();
      if (nav.startsWith("de")) return "GER";
      if (nav.startsWith("en")) return "ENG";
      return "GER";
    },

    pickLocalized(list, lang, field) {
      if (!Array.isArray(list)) return "";
      return (
        list.find((x) => x?.language === lang)?.[field] ??
        list.find((x) => x?.language === "GER")?.[field] ??
        list[0]?.[field] ??
        ""
      );
    },

    pickStepsByLanguage(stepsByLang, lang) {
      if (!Array.isArray(stepsByLang)) return [];
      return (
        stepsByLang.find((x) => x?.language === lang)?.steps ??
        stepsByLang.find((x) => x?.language === "GER")?.steps ??
        stepsByLang[0]?.steps ??
        []
      );
    },

    scrollActiveStepIntoView() {
      this.$nextTick(() => {
        const rows = this.$refs.stepRows;
        const container = this.$refs.stepsCard;
        if (!rows || !rows.length || !container) return;
        const el = rows[this.stepIndex];
        if (!el) return;
        el.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "nearest",
        });
      });
    },

    async onStepClick(idx) {
      if (idx > this.unlockedMaxIndex) return;
      await this.jumpTo(idx);
    },

    isPublicPreview(url) {
      return typeof url === "string" && url.includes(PUBLIC_PREFIX);
    },

    async resolvePreviewImage() {
      const url = this.instruction?.previewImage;
      const urlId = this.instruction?.previewImageId;
      if (!url) {
        this.previewImageSrc = "";
        return;
      }

      if (this.isPublicPreview(url)) {
        this.previewImageSrc = url;
        return;
      }

      this.previewImageLoading = true;
      try {
        const presignedUrl = await this.$store.dispatch(
          "getPresignedPublicUrl",
          { path: url, id: urlId },
        );
        this.previewImageSrc = presignedUrl || "";
      } catch {
        this.previewImageSrc = "";
      } finally {
        this.previewImageLoading = false;
      }
    },

    isFileStepType(type) {
      return [
        "pdf",
        "doc",
        "docx",
        "ppt",
        "pptx",
        "mp4",
        "mkv",
        "mov",
      ].includes(type);
    },

    isVideoType(type) {
      return ["mov", "mkv", "mp4"].includes(type);
    },

    isOfficeType(type) {
      return ["doc", "docx", "ppt", "pptx"].includes(type);
    },

    officeViewerUrl(srcUrl) {
      return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
        srcUrl,
      )}`;
    },

    async resolveCurrentStepUrl() {
      this.pdfViewerFailed = false;
      this.stepUrlError = "";
      this.resolvedStepUrl = "";
      this.resolvedDownloadUrl = "";

      if (this.stepIndex > this.unlockedMaxIndex) {
        this.stepUrlError = this.$t(
          "managedInstructionAssignment.instructionPublic.errors.stepNotUnlocked",
        );
        return;
      }

      const step = this.currentStep;
      if (!step || !step.type || !this.isFileStepType(step.type)) return;

      const location = step?.data?.[0]?.location;
      const locationId = step?.data?.[0]?.locationId;
      if (!location) return;

      this.stepUrlLoading = true;
      try {
        if (step.type === "pdf") {
          const presigned = await this.$store.dispatch("getPresignedPdf", {
            path: location,
            locationId: locationId,
            inline: true,
          });
          this.resolvedDownloadUrl = presigned;
          this.resolvedStepUrl = presigned;
          return;
        }

        const presigned = await this.$store.dispatch("getPresignedPublicUrl", {
          path: location,
          id: locationId,
        });

        this.resolvedDownloadUrl = presigned;
        this.resolvedStepUrl = presigned;
      } catch (e) {
        this.stepUrlError = this.$t(
          "managedInstructionAssignment.instructionPublic.errors.prepareFileUrl",
        );
      } finally {
        this.stepUrlLoading = false;
      }
    },

    downloadResolved() {
      if (!this.resolvedDownloadUrl) return;
      window.open(this.resolvedDownloadUrl, "_blank");
    },

    openFullscreen() {
      const ref = this.$refs.viewerWrapper;
      const node = Array.isArray(ref) ? ref[0] : ref;
      if (!node) return;

      const request =
        node.requestFullscreen ||
        node.webkitRequestFullscreen ||
        node.msRequestFullscreen;

      if (request) {
        request.call(node);
      } else if (this.resolvedStepUrl) {
        // Fullscreen API unavailable — fall back to opening in a new tab.
        window.open(this.resolvedStepUrl, "_blank");
      }
    },

    iconFor(type) {
      if (type === "pdf") return "📄";
      if (type === "doc" || type === "docx") return "📝";
      if (type === "ppt" || type === "pptx") return "📊";
      if (this.isVideoType(type)) return "🎬";
      if (type === "singleChoiceQuestion") return "✅";
      if (type === "link") return "🔗";
      return "•";
    },

    labelFor(type) {
      if (type === "pdf")
        return this.$t(
          "managedInstructionAssignment.instructionPublic.stepLabels.pdf",
        );
      if (type === "doc")
        return this.$t(
          "managedInstructionAssignment.instructionPublic.stepLabels.doc",
        );
      if (type === "docx")
        return this.$t(
          "managedInstructionAssignment.instructionPublic.stepLabels.docx",
        );
      if (type === "ppt")
        return this.$t(
          "managedInstructionAssignment.instructionPublic.stepLabels.ppt",
        );
      if (type === "pptx")
        return this.$t(
          "managedInstructionAssignment.instructionPublic.stepLabels.pptx",
        );
      if (type === "mov" || type === "mkv" || type === "mp4")
        return this.$t(
          "managedInstructionAssignment.instructionPublic.stepLabels.video",
        );
      if (type === "singleChoiceQuestion")
        return this.$t(
          "managedInstructionAssignment.instructionPublic.stepLabels.question",
        );
      if (type === "link")
        return this.$t(
          "managedInstructionAssignment.instructionPublic.stepLabels.link",
        );
      return this.$t(
        "managedInstructionAssignment.instructionPublic.stepLabels.fallback",
      );
    },

    hintFor(type) {
      if (type === "singleChoiceQuestion")
        return this.$t(
          "managedInstructionAssignment.instructionPublic.stepHints.question",
        );
      if (type === "link")
        return this.$t(
          "managedInstructionAssignment.instructionPublic.stepHints.link",
        );
      if (type === "mov" || type === "mkv" || type === "mp4")
        return this.$t(
          "managedInstructionAssignment.instructionPublic.stepHints.video",
        );
      if (
        type === "pdf" ||
        type === "doc" ||
        type === "docx" ||
        type === "ppt" ||
        type === "pptx"
      )
        return this.$t(
          "managedInstructionAssignment.instructionPublic.stepHints.document",
        );
      return "";
    },

    formatDate(iso) {
      try {
        return new Date(iso).toLocaleDateString();
      } catch {
        return iso;
      }
    },

    requiresAnswer() {
      return this.currentStep.type === "singleChoiceQuestion";
    },

    syncSelectedFromAnswers() {
      this.qError = "";
      if (this.currentStep.type !== "singleChoiceQuestion") {
        this.selectedAnswer = null;
        return;
      }

      const qid = this.questionId;
      const saved = this.answers[qid];

      this.selectedAnswer =
        saved === 0 || typeof saved === "number" ? saved : null;
    },

    async saveAnswerIfNeeded() {
      if (this.currentStep.type !== "singleChoiceQuestion") return true;

      if (this.preview) {
        if (this.selectedAnswer === null || this.selectedAnswer === undefined) {
          this.qError = this.$t(
            "managedInstructionAssignment.instructionPublic.errors.chooseAnswer",
          );
          return false;
        }

        const correctIndex = this.currentStep.data?.[0]?.correct;
        if (Number(this.selectedAnswer) !== Number(correctIndex)) {
          this.qError = this.$t(
            "managedInstructionAssignment.instructionPublic.errors.wrongAnswer",
          );
          this.selectedAnswer = null;
          return false;
        }

        this.qError = "";
        this.answers = { ...this.answers, [this.questionId]: this.selectedAnswer };
        this.unlockedMaxIndex = Math.max(
          this.unlockedMaxIndex,
          Math.min(this.stepIndex + 1, this.steps.length - 1),
        );
        return true;
      }

      const qid = this.questionId;
      if (!qid) {
        this.qError = this.$t(
          "managedInstructionAssignment.instructionPublic.errors.missingQuestionId",
        );
        return false;
      }

      if (this.selectedAnswer === null || this.selectedAnswer === undefined) {
        this.qError = this.$t(
          "managedInstructionAssignment.instructionPublic.errors.chooseAnswer",
        );
        return false;
      }

      this.qError = "";

      const { data } = await axios.post("/managedInstruction/publicAnswer", {
        token: this.token,
        questionId: qid,
        answerIndex: this.selectedAnswer,
        currentStepIndex: this.stepIndex,
      });

      if (!data?.correct) {
        this.qError =
          data?.message ||
          this.$t(
            "managedInstructionAssignment.instructionPublic.errors.wrongAnswer",
          );

        this.selectedAnswer = null;

        return false;
      }

      this.answers = { ...this.answers, [qid]: this.selectedAnswer };

      this.unlockedMaxIndex = Math.max(
        this.unlockedMaxIndex,
        Math.min(this.stepIndex + 1, this.steps.length - 1),
      );

      return true;
    },

    async nextStep() {
      const ok = await this.saveAnswerIfNeeded();
      if (!ok) return;

      if (this.currentStep.type !== "singleChoiceQuestion") {
        this.unlockedMaxIndex = Math.max(
          this.unlockedMaxIndex,
          Math.min(this.stepIndex + 1, this.steps.length - 1),
        );
      }

      if (this.stepIndex < this.steps.length - 1) {
        this.stepIndex += 1;
        this.syncSelectedFromAnswers();
      }
    },

    async prevStep() {
      if (this.stepIndex > 0) {
        this.stepIndex -= 1;
        this.syncSelectedFromAnswers();
      }
    },

    async jumpTo(idx) {
      if (idx === this.stepIndex) return;
      if (idx > this.unlockedMaxIndex) return;

      if (idx > this.stepIndex) {
        const ok = await this.saveAnswerIfNeeded();
        if (!ok) return;
      }

      this.stepIndex = idx;
      this.syncSelectedFromAnswers();
    },

    async complete() {
      const ok = await this.saveAnswerIfNeeded();
      if (!ok) return;

      if (this.preview) {
        this.completed = true;
        this.error = "";
        return;
      }

      this.submitting = true;
      try {
        await axios.post("/managedInstruction/complete", {
          token: this.token,
          answers: this.answers,
        });

        this.completed = true;
        this.error = "";
      } catch (e) {
        this.error =
          e?.response?.data?.message ||
          this.$t(
            "managedInstructionAssignment.instructionPublic.errors.completeFailed",
          );
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.pub {
  background: #f6f7fb;
  min-height: 100vh;
}

.container {
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  padding: 18px 24px;
  padding-bottom: 75px;
}

.layout {
  display: grid;
  grid-template-columns: 360px 1fr;
  gap: 16px;
}

@media (max-width: 900px) {
  .layout {
    grid-template-columns: 1fr;
  }
  .container {
    padding: 14px;
    padding-bottom: 75px;
  }
}

.card {
  background: white;
  border-radius: 14px;
  padding: 16px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.06);
}

.error {
  border: 1px solid rgba(255, 0, 0, 0.2);
}

.previewBanner {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  background: white;
  border-radius: 14px;
  padding: 12px 16px;
  margin-bottom: 16px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.06);
}

.previewBadge {
  font-weight: 700;
  text-transform: uppercase;
  font-size: 12px;
  letter-spacing: 0.04em;
  background: #111827;
  color: white;
  padding: 3px 8px;
  border-radius: 999px;
}

.previewNote {
  font-size: 13px;
  color: #666;
  flex: 1;
}

.previewLang {
  padding: 4px 8px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: white;
  color: #111827;
}

.errorText {
  color: #b00020;
  margin-top: 10px;
}

.preview {
  width: 100%;
  border-radius: 12px;
  margin-bottom: 12px;
}

.title {
  margin: 0 0 6px 0;
  font-size: 20px;
}

.desc {
  margin: 0 0 10px 0;
  color: #555;
}

.meta {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}

.badge {
  padding: 4px 10px;
  border-radius: 999px;
  background: #eee;
  font-size: 12px;
}

.due {
  font-size: 12px;
  color: #666;
}

.progressWrap {
  margin-top: 12px;
}

.progressText {
  font-size: 12px;
  color: #666;
  margin-bottom: 6px;
}

.progressBar {
  height: 10px;
  background: #eee;
  border-radius: 999px;
  overflow: hidden;
}

.progressFill {
  height: 100%;
  background: #3b82f6;
}

.unlockHint {
  margin-top: 10px;
  font-size: 12px;
  color: #666;
}

.stepsCard {
  padding: 10px;
  height: 200px;
  overflow: auto;
}

.stepRow {
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 10px;
  border-radius: 10px;
  cursor: pointer;
  user-select: none;
}

.stepRow:hover {
  background: #f4f6ff;
}

.stepRow.active {
  background: #eef2ff;
  font-weight: 600;
}

.stepRow.done {
  opacity: 0.7;
}

.stepRow.locked {
  opacity: 0.45;
  cursor: not-allowed;
}

.stepRow.locked:hover {
  background: transparent;
}

.icon {
  width: 24px;
  text-align: center;
}

.label {
  font-size: 14px;
}

.main {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.contentCard {
  min-height: 360px;
  display: flex;
  flex-direction: column;
}

.stepTop {
  margin-bottom: 12px;
}

.stepTitle {
  font-size: 16px;
  font-weight: 700;
}

.stepHint {
  font-size: 13px;
  color: #666;
  margin-top: 4px;
}

.fileRow {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}

.muted {
  color: #666;
  font-size: 13px;
  margin-top: 10px;
}

.video {
  width: 100%;
  max-height: 80vh;
  border-radius: 12px;
  background: #000;
}

.pdfContainer {
  display: flex;
  flex-direction: column;
  flex: 1;
}

.pdfWrapper {
  flex: 1;
  background: #f1f1f1;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  height: calc(100vh - 240px);
  min-height: 500px;
}

.pdfFrame {
  width: 100%;
  height: 100%;
  min-height: 500px;
  display: block;
}

@media (max-width: 900px) {
  .pdfWrapper {
    height: calc(100vh - 200px);
    min-height: 420px;
  }
  .video {
    max-height: 60vh;
  }
}

.pdfWrapper:fullscreen,
.pdfWrapper:-webkit-full-screen {
  width: 100%;
  height: 100%;
  border-radius: 0;
  background: #fff;
}

.pdfWrapper:fullscreen .pdfFrame,
.pdfWrapper:-webkit-full-screen .pdfFrame {
  height: 100%;
}

.loadingPlaceholder {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 200px;
  color: #666;
}

.question .q {
  margin: 0 0 12px 0;
  font-size: 18px;
}

.linkStep {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.linkTitle {
  margin: 0 0 10px 0;
  font-size: 18px;
}

.linkText {
  margin: 0 0 16px 0;
  color: #555;
  white-space: pre-line;
}

.linkBtn {
  display: inline-block;
  text-decoration: none;
}

.options {
  display: grid;
  gap: 10px;
}

.option {
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
}

.option.selected {
  border-color: #3b82f6;
  background: #eff6ff;
}

.option input {
  transform: scale(1.1);
}

.navBar {
  position: sticky;
  bottom: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(8px);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.06);
}

.navRight {
  display: flex;
  gap: 10px;
}

.btn {
  border: none;
  border-radius: 12px;
  padding: 10px 14px;
  background: #111827;
  color: white;
  cursor: pointer;
  font-weight: 500;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn.ghost {
  background: #eef2f7;
  color: #111827;
}

.btn.primary {
  background: #2563eb;
}

.btn.sm {
  padding: 6px 12px;
  font-size: 13px;
}

@media (min-width: 1400px) {
  .stepTitle {
    font-size: 22px;
  }
  .stepHint {
    font-size: 16px;
  }
  .question .q {
    font-size: 24px;
  }
  .option {
    font-size: 20px;
    padding: 16px 18px;
  }
  .option input {
    transform: scale(1.4);
  }
  .linkTitle {
    font-size: 24px;
  }
  .linkText {
    font-size: 18px;
  }
  .title {
    font-size: 24px;
  }
  .desc {
    font-size: 16px;
  }
}

@media (min-width: 2000px) {
  .stepTitle {
    font-size: 26px;
  }
  .stepHint {
    font-size: 18px;
  }
  .question .q {
    font-size: 30px;
  }
  .option {
    font-size: 24px;
    padding: 20px 22px;
  }
  .option input {
    transform: scale(1.7);
  }
  .linkTitle {
    font-size: 30px;
  }
  .linkText {
    font-size: 22px;
  }
}
</style>
